"use client";

import Image from "next/image";
import { FiX } from "react-icons/fi";
import TradingStyleChip from "./TradingStyleChip";
import PnLValue from "./PnLValue";
import { formatVolume, formatPct } from "@/lib/trader-format";
import { gradientForKey, initialsForKey } from "@/lib/avatar-fallback";
import type { TraderPreview } from "@/lib/market-url-preview";

interface Props {
  trader: TraderPreview;
  onDismiss?: () => void;
}

/**
 * Compact trader preview shown inside the post composer while the user
 * is typing. Mirrors the proportions of the market preview card so both
 * "kinds" of preview have the same visual weight.
 */
export default function TraderCardCompact({ trader, onDismiss }: Props) {
  const { user, stats, address, isFrenFlowUser } = trader;

  const displayName =
    user?.name || user?.username || (address ? truncateAddr(address) : "Trader");
  // username can be the raw wallet (0x…) when the user has no
  // Polymarket handle — truncate to keep it readable.
  const handle = user?.username
    ? user.username.startsWith('0x') ? truncateAddr(user.username) : `@${user.username}`
    : address
      ? truncateAddr(address)
      : "";
  const avatar = user?.image;

  return (
    <div className="mt-3 p-4 border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] bg-gray-50 dark:bg-[#0f0f12]">
      <div className="flex items-start justify-between mb-2">
        <span className="text-[10px] font-satoshi font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Trader preview
        </span>
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Remove preview"
          >
            <FiX className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="flex items-start gap-3">
        {/* Avatar — image when available; otherwise a deterministic
            colorful gradient seeded by username/address so every wallet
            still gets a recognisable visual handle. */}
        {avatar ? (
          <div className="flex-shrink-0 w-10 h-10 rounded-[5px] overflow-hidden">
            <Image
              src={avatar}
              alt={displayName}
              width={40}
              height={40}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div
            className={`flex-shrink-0 w-10 h-10 rounded-[5px] bg-gradient-to-br ${gradientForKey(
              user?.username || address || displayName,
            )} flex items-center justify-center text-white text-xs font-monument font-bold`}
          >
            {initialsForKey(user?.username || user?.name || address || displayName)}
          </div>
        )}

        {/* Identity + stats */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h4 className="font-satoshi font-semibold text-sm text-gray-900 dark:text-white truncate">
              {displayName}
            </h4>
            {handle && handle !== displayName && (
              <span className="text-[11px] font-satoshi text-gray-500 dark:text-gray-400 truncate">
                {handle}
              </span>
            )}
            {!isFrenFlowUser && (
              <span className="text-[9px] font-satoshi font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-[5px] bg-gray-200/60 dark:bg-[#0f0f12] text-gray-500 dark:text-gray-400">
                Not on FrenFlow
              </span>
            )}
          </div>

          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <TradingStyleChip style={stats?.primaryStyle} showEmpty={false} />
            <span className="text-[11px] font-satoshi text-gray-600 dark:text-gray-400">
              {stats ? (
                <>
                  {formatVolume(stats.volume)} vol
                  <span className="text-gray-400 dark:text-gray-600 mx-1">·</span>
                  <PnLValue value={stats.pnl} bold={false} />
                  {stats.winRate > 0 && (
                    <>
                      <span className="text-gray-400 dark:text-gray-600 mx-1">·</span>
                      {formatPct(stats.winRate)} WR
                    </>
                  )}
                </>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">No trading data yet</span>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function truncateAddr(addr: string): string {
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}
